import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Popover, Typography } from '@mui/material'
import EventRepeatIcon from '@mui/icons-material/EventRepeatOutlined'
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined'
import DateRangeIcon from '@mui/icons-material/DateRangeOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LocalParkingIcon from '@mui/icons-material/LocalParkingOutlined'
import { colors } from '../../const/colors'
import { IParkingPoolProfile } from '../../graphql/parkingPool/getTenantParkingPoolUsers'
import { CloseCircle } from './UserDetailsUi'

interface Limit {
  key: string
  icon: typeof ScheduleIcon
  label: string
  value: string
}

export const useProfileLimits = (profile?: IParkingPoolProfile): Limit[] => {
  const { t } = useTranslation(['user_details'])
  if (!profile) return []

  const unlimited = t('profile_limit_unlimited')
  const days = (n: number) => `${n} ${t('profile_days_short')}`

  return [
    profile.maxReservationCountPerUser != null && {
      key: 'simultaneous',
      icon: EventRepeatIcon,
      label: t('profile_limit_simultaneous'),
      value: profile.maxReservationCountPerUserUnlimited
        ? unlimited
        : String(profile.maxReservationCountPerUser),
    },
    profile.maxAdvanceDays != null && {
      key: 'advance',
      icon: ScheduleIcon,
      label: t('profile_limit_advance'),
      value: profile.maxAdvanceDaysUnlimited
        ? unlimited
        : days(profile.maxAdvanceDays),
    },
    profile.maxDurationDays != null && {
      key: 'duration',
      icon: DateRangeIcon,
      label: t('profile_limit_duration'),
      value: profile.maxDurationDaysUnlimited
        ? unlimited
        : days(profile.maxDurationDays),
    },
    profile.maxActiveUserCount != null && {
      key: 'users',
      icon: GroupOutlinedIcon,
      label: t('profile_limit_users'),
      value: profile.maxActiveUserCountUnlimited
        ? unlimited
        : String(profile.maxActiveUserCount),
    },
  ].filter((limit): limit is Limit => !!limit)
}

const getProfileParkings = (profile?: IParkingPoolProfile): string[] =>
  (profile?.parkings ?? [])
    .map(entry => entry.parking?.name)
    .filter((name): name is string => !!name)

const ProfileParkingsRow: FC<{ names: string[]; divided: boolean }> = ({
  names,
  divided,
}) => {
  const { t } = useTranslation(['user_details'])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        py: '9px',
        borderTop: divided ? `1px solid ${colors.border2}` : undefined,
      }}
    >
      <LocalParkingIcon
        sx={{ fontSize: 16, color: colors.slate, flexShrink: 0, mt: '2px' }}
      />
      <Typography
        sx={{
          flexShrink: 0,
          fontSize: '12px',
          fontWeight: 600,
          color: colors.textMuted,
          mt: '2px',
        }}
      >
        {t('profile_parkings')}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: '5px',
        }}
      >
        {names.map(name => (
          <Box
            key={name}
            component="span"
            sx={{
              background: colors.rowSelected,
              color: colors.navy,
              fontSize: '11px',
              fontWeight: 700,
              p: '3px 8px',
              borderRadius: colors.radiusTag,
            }}
          >
            {name}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export const ProfileLimitGrid: FC<{
  profile?: IParkingPoolProfile
  framed?: boolean
}> = ({ profile, framed }) => {
  const { t } = useTranslation(['user_details'])
  const limits = useProfileLimits(profile)
  const parkings = getProfileParkings(profile)

  if (!limits.length && !parkings.length) {
    return (
      <Typography
        sx={{
          fontSize: '12px',
          color: colors.textMuted,
          mt: framed ? 0 : '8px',
        }}
      >
        {t('profile_no_limits')}
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        mt: '10px',
        display: 'flex',
        flexDirection: 'column',
        ...(framed && {
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          p: '4px 14px',
        }),
      }}
    >
      {limits.map((limit, i) => (
        <Box
          key={limit.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            py: '9px',
            borderTop: i ? `1px solid ${colors.border2}` : undefined,
          }}
        >
          <limit.icon
            sx={{ fontSize: 16, color: colors.slate, flexShrink: 0 }}
          />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '12px',
              fontWeight: 600,
              color: colors.textMuted,
            }}
          >
            {limit.label}
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 800,
              color: colors.navy,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {limit.value}
          </Typography>
        </Box>
      ))}
      {!!parkings.length && (
        <ProfileParkingsRow names={parkings} divided={!!limits.length} />
      )}
    </Box>
  )
}

const Badge: FC<{ text: string; tone: 'navy' | 'green' }> = ({
  text,
  tone,
}) => (
  <Box
    component="span"
    sx={{
      background: tone === 'green' ? colors.greenBg : colors.rowSelected,
      color: tone === 'green' ? colors.greenBgText : colors.navy,
      fontSize: '10.5px',
      fontWeight: 800,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      p: '3px 8px',
      borderRadius: colors.radiusTag,
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </Box>
)

const LegendEntry: FC<{
  profile: IParkingPoolProfile
  isDefault: boolean
  isAssigned: boolean
}> = ({ profile, isDefault, isAssigned }) => {
  const { t } = useTranslation(['user_details'])
  const userCount = profile.usersAggregate?.count

  return (
    <Box
      sx={{
        border: `1px solid ${isAssigned ? colors.borderCheck : colors.border}`,
        background: isAssigned ? colors.bg : colors.surface,
        borderRadius: '14px',
        p: '13px 14px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{ fontSize: '13.5px', fontWeight: 800, color: colors.navy }}
        >
          {profile.name}
        </Typography>
        {isAssigned && (
          <Badge text={t('profile_badge_assigned')} tone="green" />
        )}
        {isDefault && <Badge text={t('profile_badge_default')} tone="navy" />}
      </Box>

      {userCount != null && (
        <Typography
          sx={{
            fontSize: '11.5px',
            color: colors.textMuted,
            fontWeight: 600,
            mt: '3px',
          }}
        >
          {userCount} {t('profile_users_assigned')}
        </Typography>
      )}

      <ProfileLimitGrid profile={profile} />

      {!!profile.customEmailMessage && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '7px',
            mt: '11px',
            pt: '10px',
            borderTop: `1px solid ${colors.border2}`,
            fontSize: '11.5px',
            color: colors.textMuted,
            fontWeight: 600,
            lineHeight: 1.45,
          }}
        >
          <MailOutlineIcon
            sx={{ fontSize: 14, color: colors.slate, flexShrink: 0, mt: '1px' }}
          />
          <Box sx={{ minWidth: 0 }}>
            {t('profile_email_note')}
            <Box
              component="span"
              sx={{
                color: colors.textSecondary,
                fontStyle: 'italic',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              „{profile.customEmailMessage}”
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const ProfileLegend: FC<{
  anchorEl: HTMLElement | null
  onClose: () => void
  profiles: IParkingPoolProfile[]
  defaultProfileId?: string
  assignedProfileId?: string
}> = ({ anchorEl, onClose, profiles, defaultProfileId, assignedProfileId }) => {
  const { t } = useTranslation(['user_details'])

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: '8px',
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(70vh, 620px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '18px',
            border: `1px solid ${colors.border2}`,
            boxShadow: '0 24px 48px -24px rgba(19,54,73,.45)',
            p: '18px',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          mb: '12px',
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: '14.5px', fontWeight: 800, color: colors.navy }}
          >
            {t('profile_legend_title')}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: colors.textMuted,
              fontWeight: 600,
              lineHeight: 1.5,
              mt: '3px',
            }}
          >
            {t('profile_legend_intro')}
          </Typography>
        </Box>
        <CloseCircle onClick={onClose} size={28} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '9px',
          minHeight: 0,
          overflowY: 'auto',
          mr: '-8px',
          pr: '8px',
        }}
      >
        {profiles.length ? (
          profiles.map(profile => (
            <LegendEntry
              key={profile.id}
              profile={profile}
              isDefault={profile.id === defaultProfileId}
              isAssigned={profile.id === assignedProfileId}
            />
          ))
        ) : (
          <Typography sx={{ fontSize: '12.5px', color: colors.textMuted }}>
            {t('parking_pool:no_profiles_available')}
          </Typography>
        )}
      </Box>
    </Popover>
  )
}

export default ProfileLegend
